/**
 * Centralised configuration for the provider intro screens (mobile
 * ProviderIntroScreen, webapp ProviderIntroPage) and the signup banner. The
 * shape is consumed by both apps; logos are resolved per-app because Metro and
 * Vite handle asset bundling differently.
 *
 * Only what a screen reads lives here. The per-field label/hint keys and the
 * step-2 message key it once carried were never read: each app's connect
 * screen names its own fields and maps step 2 through its own message table
 * (FE #307 follow-up).
 */

export type ProviderId =
  | 'wunderground'
  | 'ecowitt'
  | 'ambientWeather'
  | 'davis'
  | 'weatherflow'
  | 'myAcurite'
  | 'netatmoWeather'
  | 'netatmoEnergy';

export type CredentialKind = 'apiKey' | 'oauth' | 'emailPassword';

export interface ProviderConnectConfig {
  id: ProviderId;
  /** Brand name (not localised). */
  displayName: string;
  /** Asset id resolved per-app to a logo image source. */
  logoAssetId: string;
  kind: CredentialKind;
  /** Help-center article slug, e.g. "wunderground-setup". */
  helpArticleSlug: string;
  /** Primary URL surfaced in step 2 (where users get the credential). */
  primaryRetrieveUrl?: string;
}

const WUNDERGROUND_CONFIG: ProviderConnectConfig = {
  id: 'wunderground',
  displayName: 'Weather Underground',
  logoAssetId: 'wunderground',
  kind: 'apiKey',
  helpArticleSlug: 'wunderground-setup',
  primaryRetrieveUrl: 'https://www.wunderground.com/member/api-keys',
};

const ECOWITT_CONFIG: ProviderConnectConfig = {
  id: 'ecowitt',
  displayName: 'Ecowitt',
  logoAssetId: 'ecowitt',
  kind: 'apiKey',
  helpArticleSlug: 'ecowitt-setup',
  primaryRetrieveUrl: 'https://www.ecowitt.net/home/user',
};

const AMBIENT_WEATHER_CONFIG: ProviderConnectConfig = {
  id: 'ambientWeather',
  displayName: 'Ambient Weather',
  logoAssetId: 'ambientWeather',
  kind: 'apiKey',
  helpArticleSlug: 'ambient-weather-setup',
  primaryRetrieveUrl: 'https://ambientweather.net/account',
};

const DAVIS_CONFIG: ProviderConnectConfig = {
  id: 'davis',
  displayName: 'Davis Instruments',
  logoAssetId: 'davis',
  kind: 'apiKey',
  helpArticleSlug: 'davis-setup',
  primaryRetrieveUrl: 'https://www.weatherlink.com/account',
};

const WEATHERFLOW_CONFIG: ProviderConnectConfig = {
  id: 'weatherflow',
  displayName: 'Weatherflow Tempest',
  logoAssetId: 'weatherflow',
  kind: 'apiKey',
  helpArticleSlug: 'weatherflow-setup',
  primaryRetrieveUrl: 'https://tempestwx.com/settings/tokens',
};

const MY_ACURITE_CONFIG: ProviderConnectConfig = {
  id: 'myAcurite',
  displayName: 'My AcuRite',
  logoAssetId: 'myAcurite',
  kind: 'emailPassword',
  helpArticleSlug: 'myacurite-setup',
};

const NETATMO_WEATHER_CONFIG: ProviderConnectConfig = {
  id: 'netatmoWeather',
  displayName: 'Netatmo Weather',
  logoAssetId: 'netatmoWeather',
  kind: 'oauth',
  helpArticleSlug: 'netatmo-setup',
};

const NETATMO_ENERGY_CONFIG: ProviderConnectConfig = {
  id: 'netatmoEnergy',
  displayName: 'Netatmo Energy',
  logoAssetId: 'netatmoEnergy',
  kind: 'oauth',
  helpArticleSlug: 'netatmo-energy-setup',
};

export const PROVIDER_CONNECT_CONFIG: Record<
  ProviderId,
  ProviderConnectConfig
> = {
  wunderground: WUNDERGROUND_CONFIG,
  ecowitt: ECOWITT_CONFIG,
  ambientWeather: AMBIENT_WEATHER_CONFIG,
  davis: DAVIS_CONFIG,
  weatherflow: WEATHERFLOW_CONFIG,
  myAcurite: MY_ACURITE_CONFIG,
  netatmoWeather: NETATMO_WEATHER_CONFIG,
  netatmoEnergy: NETATMO_ENERGY_CONFIG,
};

export const PROVIDER_IDS: ProviderId[] = Object.keys(
  PROVIDER_CONNECT_CONFIG,
) as ProviderId[];
